import { getAccessToken } from '@/services/tokenStore';

const API_BASE_URL = (process.env.VUE_APP_API_BASE_URL || 'http://localhost:5001/api').replace(/\/$/, '');
const SESSION_STORAGE_KEY = 'qp_analytics_session_id';

function clip(value, maxLength = 255) {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\s+/g, ' ').trim().slice(0, maxLength);
}

function getSessionId() {
  try {
    const existing = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (existing) return existing;

    const next = window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    sessionStorage.setItem(SESSION_STORAGE_KEY, next);
    return next;
  } catch (_) {
    return '';
  }
}

function currentPath() {
  if (typeof window === 'undefined') return '';
  // Query-string и hash не логируем — там могут быть токены/PII, и они раздувают путь.
  return window.location.pathname;
}

function safeDetails(details = {}) {
  const safe = {};
  Object.entries(details || {}).slice(0, 50).forEach(([key, value]) => {
    if (value === undefined || typeof value === 'function') return;
    if (value === null || ['string', 'number', 'boolean'].includes(typeof value)) {
      safe[clip(key, 120)] = typeof value === 'string' ? clip(value, 500) : value;
    }
  });
  return safe;
}

// Логируем только явно помеченные элементы, а не каждый кликабельный узел.
// Это на порядок сокращает объём user_activity_logs. Чтобы залогировать
// клик, добавьте элементу атрибут data-analytics-track (или data-analytics-click),
// либо вызовите trackClick() из useAnalytics для точечных событий.
function findClickableElement(target) {
  if (!target || !target.closest) return null;
  return target.closest('[data-analytics-track], [data-analytics-click]');
}

function getElementLabel(element) {
  if (!element) return '';
  const tagName = (element.tagName || '').toLowerCase();
  const label =
    element.getAttribute('data-analytics-label') ||
    element.getAttribute('aria-label') ||
    element.getAttribute('title') ||
    element.getAttribute('name') ||
    (tagName === 'input' ? element.getAttribute('value') : '') ||
    element.innerText ||
    element.textContent ||
    tagName;

  return clip(label, 255);
}

function getElementRole(element) {
  if (!element) return '';
  return clip(element.getAttribute('role') || (element.tagName || '').toLowerCase(), 50);
}

function shouldIgnoreClick(element) {
  if (!element) return true;
  if (element.closest('[data-analytics-ignore="true"]')) return true;
  if (element.disabled || element.getAttribute('aria-disabled') === 'true') return true;
  return false;
}

// Google Analytics + backend user activity service for SPA
class AnalyticsService {
  constructor() {
    this.gtag = null;
    this.initialized = false;
    this.gaWarningShown = false;
    this.backendQueue = [];
    this.flushTimer = null;
    this.flushInProgress = false;
    this.clickTrackingInstalled = false;
    this.sessionId = typeof window !== 'undefined' ? getSessionId() : '';
  }

  // Инициализация Google Analytics
  init() {
    if (typeof window !== 'undefined' && window.gtag) {
      this.gtag = window.gtag;
      this.initialized = true;
      console.log('Google Analytics initialized');
    }
  }

  installAutoTracking() {
    if (this.clickTrackingInstalled || typeof document === 'undefined') {
      return;
    }

    document.addEventListener('click', (event) => {
      const element = findClickableElement(event.target);
      if (shouldIgnoreClick(element)) return;

      const label = getElementLabel(element);
      this.trackBackendEvent({
        event_type: 'click',
        event_name: 'click',
        path: currentPath(),
        element_label: label,
        element_role: getElementRole(element),
        details: {
          source: 'auto_click',
          tag: (element.tagName || '').toLowerCase(),
          href: element.tagName === 'A' ? clip(element.getAttribute('href'), 500) : undefined,
        },
      });
    }, true);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        this.flushBackendQueue();
      }
    });

    this.clickTrackingInstalled = true;
  }

  trackBackendEvent(event = {}) {
    const token = getAccessToken();
    if (!token || token === 'null' || token === 'undefined') {
      return;
    }

    this.backendQueue.push({
      event_type: clip(event.event_type || 'custom', 40) || 'custom',
      event_name: clip(event.event_name || event.event_type || 'custom', 120),
      path: clip(event.path || currentPath(), 500),
      route_name: clip(event.route_name, 120),
      element_label: clip(event.element_label, 255),
      element_role: clip(event.element_role, 50),
      session_id: this.sessionId,
      client_timestamp: new Date().toISOString(),
      details: safeDetails(event.details),
    });

    if (this.backendQueue.length >= 10) {
      this.flushBackendQueue();
      return;
    }

    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => this.flushBackendQueue(), 2000);
    }
  }

  async flushBackendQueue() {
    if (this.flushInProgress || !this.backendQueue.length) {
      return;
    }

    const token = getAccessToken();
    if (!token || token === 'null' || token === 'undefined') {
      this.backendQueue = [];
      return;
    }

    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    const events = this.backendQueue.splice(0, 50);
    this.flushInProgress = true;

    try {
      await fetch(`${API_BASE_URL}/analytics/events`, {
        method: 'POST',
        credentials: 'include',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ events }),
      });
    } catch (error) {
      console.warn('Backend analytics event was not sent:', error);
    } finally {
      this.flushInProgress = false;
      if (this.backendQueue.length) {
        this.flushBackendQueue();
      }
    }
  }

  // Отслеживание просмотров страниц
  trackPageView(path, title = '', routeName = '') {
    if (!this.initialized || !this.gtag) {
      if (!this.gaWarningShown) {
        console.warn('Google Analytics not initialized');
        this.gaWarningShown = true;
      }
    } else {
      try {
        this.gtag('config', 'G-3LV5JTG361', {
          page_path: path,
          page_title: title || document.title,
        });
        
        console.log('Page view tracked:', path);
      } catch (error) {
        console.error('Error tracking page view:', error);
      }
    }

    this.trackBackendEvent({
      event_type: 'page_view',
      event_name: 'page_view',
      path,
      route_name: routeName,
      details: {
        title: title || (typeof document !== 'undefined' ? document.title : ''),
        source: 'router',
      },
    });
  }

  trackBackendFromEvent(eventName, parameters = {}) {
    const eventType = eventName === 'click' ? 'click' : 'custom';
    this.trackBackendEvent({
      event_type: eventType,
      event_name: eventName,
      path: parameters.page_path || currentPath(),
      element_label: parameters.event_label,
      details: {
        ...parameters,
        source: parameters.source || 'manual',
      },
    });
  }

  // Отслеживание событий
  trackEvent(eventName, parameters = {}) {
    if (!this.initialized || !this.gtag) {
      if (!this.gaWarningShown) {
        console.warn('Google Analytics not initialized');
        this.gaWarningShown = true;
      }
    } else {
      try {
        this.gtag('event', eventName, parameters);
        console.log('Event tracked:', eventName, parameters);
      } catch (error) {
        console.error('Error tracking event:', error);
      }
    }

    this.trackBackendFromEvent(eventName, parameters);
  }

  // Отслеживание конверсий
  trackConversion(conversionName, value = null) {
    const parameters = {
      event_category: 'conversion',
      event_label: conversionName,
    };

    if (value !== null) {
      parameters.value = value;
    }

    this.trackEvent('conversion', parameters);
  }

  // Отслеживание пользовательских действий
  trackUserAction(action, category = 'user_interaction', label = '') {
    this.trackEvent('user_action', {
      event_category: category,
      event_label: label,
      action: action,
    });
  }
}

// Создаем единственный экземпляр сервиса
const analyticsService = new AnalyticsService();

export default analyticsService; 
